import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Box, Typography } from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import { SvgIconComponent } from "@mui/icons-material";
import PrologueImage from "../components/PrologueImage";
import BodyCard from "../components/BodyCard";
import Section from "../components/Section";
import CustomCircularProgress from "../components/CustomCircularProgress";
import IconBulletList from "../components/IconBulletList";
import { useCourses } from "../hooks/useCourses";
import { useTheses } from "../hooks/useThesis";
import SimpleTextPage from "../components/SimpleTextPage";
import { getCourseDetailsText } from "../utils/getCourseDetailsText";

const courseIcons: Record<string, SvgIconComponent> = {
  undergraduate: SchoolOutlinedIcon,
  postgraduate: AutoStoriesOutlinedIcon,
};

const thesisIcons: Record<string, SvgIconComponent> = {
  current: LightbulbOutlinedIcon,
  completed: TaskAltOutlinedIcon,
};

export default function CoursesTheses() {
  const { t } = useTranslation();
  const courses = useCourses();
  const theses = useTheses();

  const emptyText = t("coursesThesesEmpty");

  const message = (text: string) => (
    <Typography sx={{ color: "#62605A", textAlign: "center" }}>
      {text}
    </Typography>
  );

  const coursesContent = () => {
    if (courses.isLoading) return <CustomCircularProgress color="#C46A2D" />;
    if (courses.isError || !courses.data) return message(t("coursesError"));

    return (
      <Grid container spacing={{ xs: 5, md: 6 }}>
        {courses.data.groups.map((group) => (
          <Grid item xs={12} md={6} key={group.id}>
            <IconBulletList
              title={t(group.id)}
              icon={courseIcons[group.id] ?? SchoolOutlinedIcon}
              emptyText={emptyText}
              items={group.courses.map((course) => ({
                id: course.id,
                primary: course.title,
                secondary: getCourseDetailsText(course),
                url: course.url,
              }))}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  const thesesContent = () => {
    if (theses.isLoading) return <CustomCircularProgress color="#C46A2D" />;
    if (theses.isError || !theses.data) return message(t("thesesError"));

    return (
      <Grid container spacing={{ xs: 5, md: 6 }}>
        {theses.data.groups.map((group) => (
          <Grid item xs={12} md={6} key={group.id}>
            <IconBulletList
              title={t(`${group.id}Theses`)}
              icon={thesisIcons[group.id] ?? LightbulbOutlinedIcon}
              emptyText={emptyText}
              linkOn="secondary"
              items={group.theses.map((thesis) => ({
                id: thesis.id,
                primary: thesis.student,
                secondary: thesis.title,
                url: thesis.url,
              }))}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  const content = () => (
    <Box>
      <Box sx={{ mb: { xs: 5, md: 7 } }}>{coursesContent()}</Box>
      <Box>{thesesContent()}</Box>
    </Box>
  );

  return (
    <>
      <Helmet>
        <title>{`${t("coursesTheses")} | PEL Group`}</title>
        <meta name="description" content={t("coursesThesesIntro")} />
      </Helmet>
      <PrologueImage
        title={t("coursesTheses")}
        subtitle={t("coursesThesesIntro")}
        image="/home-carousel-3.jpg"
      />
      <BodyCard>
        <SimpleTextPage
          noCard={true}
          pageId="af619793-2628-4d30-9000-e382a5dfe3f1"
        />
        <Section content={content()} />
      </BodyCard>
    </>
  );
}
